import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';

export const JOURNAL_CURRENCIES = ['USD', 'GBP', 'EUR', 'JPY', 'CAD', 'AUD', 'CHF', 'NZD'];
export function validJournalBalance(value) {
  const balance = Number(value?.balance);
  return Number.isFinite(balance) && balance > 0 && balance <= 1e12 && JOURNAL_CURRENCIES.includes(value?.currency) ? { balance, currency: value.currency } : null;
}
const key = uid => `tradelog-journal-currency:${uid}`;
function cache(uid) { try { return uid ? JSON.parse(localStorage.getItem(key(uid)) || '{}') || {} : {}; } catch { return {}; } }
function remember(uid, value, pending) { try { localStorage.setItem(key(uid), JSON.stringify({ value, pending })); } catch {} }
export function useJournalCurrency(uid) {
  const [state, setState] = useState(() => ({ uid, value: validJournalBalance(cache(uid).value), status: '' }));
  const session = useRef(null);
  const write = useCallback(async (scope, value) => {
    const version = ++scope.version;
    scope.touched = true; remember(scope.uid, value, true);
    setState({ uid: scope.uid, value, status: 'saving' });
    try {
      // Serialize choices so a slow older save cannot replace the newest balance.
      scope.queue = scope.queue.catch(() => {}).then(() => setDoc(doc(db, 'users', scope.uid, 'meta', 'returnDisplay'), value || { balance: 0, currency: 'USD' }));
      await scope.queue;
      if (session.current !== scope || !scope.active || version !== scope.version) return false;
      remember(scope.uid, value, false); setState({ uid: scope.uid, value, status: 'saved' }); return true;
    } catch {
      if (session.current === scope && scope.active && version === scope.version) setState({ uid: scope.uid, value, status: 'local' });
      return false;
    }
  }, []);
  useEffect(() => {
    const stored = cache(uid), initial = validJournalBalance(stored.value);
    const scope = { uid, active: true, touched: false, version: 0, queue: Promise.resolve() }; session.current = scope;
    setState({ uid, value: initial, status: '' });
    if (uid) getDoc(doc(db, 'users', uid, 'meta', 'returnDisplay')).then(snapshot => {
      if (!scope.active || scope.touched) return;
      if (stored.pending) { write(scope, initial); return; }
      if (snapshot.exists()) { const value = validJournalBalance(snapshot.data()); remember(uid, value, false); setState({ uid, value, status: '' }); }
    }).catch(() => { if (scope.active && !scope.touched && stored.pending) write(scope, initial); });
    return () => { scope.active = false; };
  }, [uid, write]);
  const save = useCallback(value => {
    const scope = session.current;
    if (!uid || scope?.uid !== uid || !scope.active) return Promise.resolve(false);
    const next = value === null ? null : validJournalBalance(value);
    if (value !== null && !next) return Promise.resolve(false);
    return write(scope, next);
  }, [uid, write]);
  return { value: state.uid === uid ? state.value : validJournalBalance(cache(uid).value), status: state.uid === uid ? state.status : '', save };
}
